import { THEMES } from '../contexts/ThemeContext';

/**
 * Generate CSS classes based on current theme
 */
export const getThemeClasses = (theme) => {
  const baseClasses = {
    // Background classes
    background: {
      primary: 'bg-[var(--color-background)]',
      surface: 'bg-[var(--color-surface)]',
      surfaceSecondary: 'bg-[var(--color-surface-secondary)]'
    },
    
    // Text classes
    text: {
      primary: 'text-[var(--color-text)]',
      secondary: 'text-[var(--color-text-secondary)]',
      muted: 'text-[var(--color-text-muted)]',
      success: 'text-[var(--color-success)]',
      warning: 'text-[var(--color-warning)]',
      error: 'text-[var(--color-error)]',
      info: 'text-[var(--color-info)]'
    },
    
    // Border classes
    border: {
      primary: 'border-[var(--color-border)]',
      surface: 'border-[var(--color-surface)]'
    },
    
    // Button classes
    button: {
      primary: 'bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-white',
      secondary: 'bg-[var(--color-secondary)] hover:bg-[var(--color-secondary)] text-white',
      outline: 'border-[var(--color-primary)] text-[var(--color-primary)] hover:bg-[var(--color-primary)] hover:text-white',
      ghost: 'text-[var(--color-text-secondary)] hover:bg-[var(--color-surface)] hover:text-[var(--color-text)]'
    },
    
    // Card classes
    card: {
      base: 'bg-[var(--color-surface)] border-[var(--color-border)] text-[var(--color-text)]',
      hover: 'hover:bg-[var(--color-surface-secondary)] transition-colors'
    },
    
    // Input classes
    input: {
      base: 'bg-[var(--color-surface)] border-[var(--color-border)] text-[var(--color-text)] placeholder-[var(--color-text-muted)]',
      focus: 'focus:border-[var(--color-primary)] focus:ring-[var(--color-primary)]'
    }
  };

  return baseClasses;
};

/**
 * Generate team-specific color palette
 */
export const generateTeamTheme = (teamName, primaryColor, secondaryColor) => {
  const baseTheme = THEMES.dark;
  
  return {
    ...baseTheme,
    id: `team_${teamName.toLowerCase().replace(/\s+/g, '_')}`,
    name: `${teamName} Theme`,
    description: `Custom theme for ${teamName}`,
    colors: {
      ...baseTheme.colors,
      primary: primaryColor,
      primaryHover: adjustBrightness(primaryColor, -20),
      secondary: secondaryColor,
      info: primaryColor
    }
  };
};

/**
 * Adjust color brightness
 */
export const adjustBrightness = (hex, percent) => {
  // Remove # if present
  hex = hex.replace(/^#/, '');
  
  // Parse hex to RGB
  const num = parseInt(hex, 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) + amt;
  const G = (num >> 8 & 0x00FF) + amt;
  const B = (num & 0x0000FF) + amt;
  
  return '#' + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
    (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
    (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1);
};

/**
 * Check if color is light or dark
 */
export const isLightColor = (hex) => {
  hex = hex.replace(/^#/, '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const brightness = ((r * 299) + (g * 587) + (b * 114)) / 1000;
  return brightness > 155;
};

/**
 * Get contrast color (black or white)
 */
export const getContrastColor = (hex) => {
  return isLightColor(hex) ? '#000000' : '#FFFFFF';
};

/**
 * Generate CSS custom properties from theme
 */
export const generateThemeCSS = (theme) => {
  let css = ':root {\n';
  
  Object.entries(theme.colors).forEach(([key, value]) => {
    const cssVar = `--color-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
    css += `  ${cssVar}: ${value};\n`;
  });
  
  if (theme.accessibility) {
    css += `  --text-scale: ${theme.accessibility.textScale};\n`;
  }
  
  css += '}\n';
  return css;
};

/**
 * Apply theme classes to element
 */
export const applyThemeToElement = (element, themeClasses, classType, variant = 'base') => {
  if (!element || !themeClasses[classType]) return;
  
  const classes = themeClasses[classType][variant];
  if (classes) {
    element.className = classes;
  }
};

/**
 * Get team logo placeholder
 */
export const getTeamLogoPlaceholder = (teamName, colors) => {
  const initials = teamName
    .split(' ')
    .map(word => word[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();
    
  return {
    initials,
    backgroundColor: colors.primary || '#6B7280',
    textColor: getContrastColor(colors.primary || '#6B7280')
  };
};

/**
 * Popular team color presets
 */
export const TEAM_COLOR_PRESETS = {
  arsenal: { primary: '#EF0107', secondary: '#023474', name: 'Arsenal' },
  chelsea: { primary: '#034694', secondary: '#DBA111', name: 'Chelsea' },
  liverpool: { primary: '#C8102E', secondary: '#F6EB61', name: 'Liverpool' },
  manchester_city: { primary: '#6CABDD', secondary: '#1C2C5B', name: 'Manchester City' },
  manchester_united: { primary: '#FF0000', secondary: '#FFE500', name: 'Manchester United' },
  tottenham: { primary: '#132257', secondary: '#FFFFFF', name: 'Tottenham' },
  leicester: { primary: '#003090', secondary: '#FDBB30', name: 'Leicester City' },
  west_ham: { primary: '#7A263A', secondary: '#1BB1E7', name: 'West Ham' },
  everton: { primary: '#003399', secondary: '#FFFFFF', name: 'Everton' },
  newcastle: { primary: '#241F20', secondary: '#FFFFFF', name: 'Newcastle' },
  brighton: { primary: '#0057B8', secondary: '#FFCD00', name: 'Brighton' },
  aston_villa: { primary: '#95BFE5', secondary: '#670E36', name: 'Aston Villa' }
};

export default {
  getThemeClasses,
  generateTeamTheme,
  adjustBrightness,
  isLightColor,
  getContrastColor,
  generateThemeCSS,
  applyThemeToElement,
  getTeamLogoPlaceholder,
  TEAM_COLOR_PRESETS
};